// src/Admin/EnrollManagement.jsx — applicants (search / filter / status / CSV) + program enrollment switches

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../Firebase/firebaseConfig';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import ConfirmDialog from './ConfirmDialog';
import { STATUSES, enrollmentStatus, toDate, formatDate, downloadCsv } from './utils';

const pickName = (obj) => obj?.en || (obj && Object.values(obj).find(Boolean)) || '';

const EnrollManagement = ({ enrollments }) => {
  const [tab, setTab] = useState('applicants');
  const [courses, setCourses] = useState([]);
  const [diplomas, setDiplomas] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [notice, setNotice] = useState(null); // { type, text }

  // filters
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [sort, setSort] = useState('newest');

  const [toDelete, setToDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef();

  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([getDocs(collection(db, 'courses')), getDocs(collection(db, 'diplomas'))]);
        setCourses(c.docs.map((x) => ({ id: x.id, ...x.data() })));
        setDiplomas(d.docs.map((x) => ({ id: x.id, ...x.data() })));
      } catch (err) {
        console.error('Could not load programs', err);
        setNotice({ type: 'warn', text: 'Could not load the program list.' });
      } finally {
        setProgramsLoading(false);
      }
    })();
  }, []);

  const flash = (type, text) => {
    setNotice({ type, text });
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setNotice(null), 4000);
  };

  const programNames = useMemo(() => [...new Set(enrollments.map((e) => e.programName).filter(Boolean))].sort(), [enrollments]);

  const counts = useMemo(() => {
    const c = { all: enrollments.length, new: 0, contacted: 0, done: 0 };
    enrollments.forEach((e) => { c[enrollmentStatus(e)] += 1; });
    return c;
  }, [enrollments]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = enrollments.filter(
      (e) =>
        (typeFilter === 'all' || e.programType === typeFilter) &&
        (statusFilter === 'all' || enrollmentStatus(e) === statusFilter) &&
        (programFilter === 'all' || e.programName === programFilter) &&
        (!q || [e.name, e.email, e.phone, e.programName].some((v) => String(v || '').toLowerCase().includes(q)))
    );
    const time = (e) => toDate(e.timestamp)?.getTime() || 0;
    return [...list].sort((a, b) => (sort === 'oldest' ? time(a) - time(b) : time(b) - time(a)));
  }, [enrollments, query, typeFilter, statusFilter, programFilter, sort]);

  const isFiltered = query || typeFilter !== 'all' || statusFilter !== 'all' || programFilter !== 'all';
  const clearFilters = () => { setQuery(''); setTypeFilter('all'); setStatusFilter('all'); setProgramFilter('all'); };

  const setStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, 'enrollments', id), { status });
    } catch (err) {
      console.error(err);
      flash('error', 'Could not update the status.');
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await deleteDoc(doc(db, 'enrollments', toDelete.id));
      flash('success', 'Enrollment deleted.');
    } catch (err) {
      console.error(err);
      flash('error', 'Could not delete the enrollment.');
    } finally {
      setBusy(false);
      setToDelete(null);
    }
  };

  const exportCsv = () =>
    downloadCsv(
      `enrollments-${new Date().toISOString().slice(0, 10)}.csv`,
      rows.map((e) => ({
        Name: e.name,
        Email: e.email,
        Phone: e.phone,
        Program: e.programName,
        Type: e.programType,
        Status: enrollmentStatus(e),
        Date: toDate(e.timestamp)?.toISOString().slice(0, 10) || '',
      }))
    );

  const toggleOpen = async (type, program) => {
    const col = type === 'course' ? 'courses' : 'diplomas';
    try {
      await updateDoc(doc(db, col, program.id), { enrollmentOpen: !program.enrollmentOpen });
      const patch = (list) => list.map((p) => (p.id === program.id ? { ...p, enrollmentOpen: !program.enrollmentOpen } : p));
      type === 'course' ? setCourses(patch) : setDiplomas(patch);
      flash('success', `Enrollment ${program.enrollmentOpen ? 'closed' : 'opened'}.`);
    } catch (err) {
      console.error(err);
      flash('error', 'Could not change the enrollment status.');
    }
  };

  const applicantsByProgram = useMemo(() => {
    const m = {};
    enrollments.forEach((e) => { m[e.programId] = (m[e.programId] || 0) + 1; });
    return m;
  }, [enrollments]);

  const renderPrograms = (title, list, type, nameOf) => (
    <section className="adm-panel">
      <header className="adm-panel__head"><h2>{title}</h2><span className="adm-pill">{list.length}</span></header>
      {list.length ? (
        <ul className="adm-programs">
          {list.map((p) => (
            <li key={p.id}>
              <div>
                <strong>{nameOf(p) || 'Untitled'}</strong>
                <small>{applicantsByProgram[p.id] || 0} applicant{(applicantsByProgram[p.id] || 0) === 1 ? '' : 's'}</small>
              </div>
              <button
                className={`adm-switch ${p.enrollmentOpen ? 'is-on' : ''}`}
                onClick={() => toggleOpen(type, p)}
                role="switch"
                aria-checked={!!p.enrollmentOpen}
                aria-label={`${p.enrollmentOpen ? 'Close' : 'Open'} enrollment for ${nameOf(p)}`}
              >
                <span></span>
                <em>{p.enrollmentOpen ? 'Open' : 'Closed'}</em>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="adm-empty">Nothing here yet.</p>
      )}
    </section>
  );

  return (
    <div className="adm-screen">
      {notice && <div className={`adm-alert adm-alert--${notice.type}`} role="status">{notice.text}</div>}

      <div className="adm-tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'applicants'} className={tab === 'applicants' ? 'is-active' : ''} onClick={() => setTab('applicants')}>
          Applicants <span className="adm-pill">{counts.all}</span>
        </button>
        <button role="tab" aria-selected={tab === 'programs'} className={tab === 'programs' ? 'is-active' : ''} onClick={() => setTab('programs')}>
          Program status
        </button>
      </div>

      {tab === 'applicants' ? (
        <>
          <div className="adm-summary">
            {[['all', 'All'], ['new', 'New'], ['contacted', 'Contacted'], ['done', 'Done']].map(([key, label]) => (
              <button key={key} className={`adm-summary__item ${statusFilter === key ? 'is-active' : ''}`} onClick={() => setStatusFilter(key)}>
                <strong>{counts[key]}</strong> {label}
              </button>
            ))}
          </div>

          <div className="adm-toolbar">
            <div className="adm-search">
              <i className="fas fa-search"></i>
              <input type="search" placeholder="Search name, email, phone or program…" value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Program type">
              <option value="all">All types</option>
              <option value="course">Courses</option>
              <option value="diploma">Diplomas</option>
            </select>
            <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)} aria-label="Program">
              <option value="all">All programs</option>
              {programNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <button className="adm-btn adm-btn--primary" onClick={exportCsv} disabled={!rows.length}>
              <i className="fas fa-file-arrow-down"></i> Export CSV
            </button>
          </div>

          <p className="adm-count">
            Showing <strong>{rows.length}</strong> of {counts.all}
            {isFiltered && <button className="adm-link" onClick={clearFilters}>Clear filters</button>}
          </p>

          {rows.length ? (
            <div className="adm-table-wrap">
              <table className="adm-table adm-table--stack">
                <thead>
                  <tr><th>Applicant</th><th>Program</th><th>Date</th><th>Status</th><th className="adm-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {rows.map((e) => {
                    const status = enrollmentStatus(e);
                    return (
                      <tr key={e.id}>
                        <td data-label="Applicant">
                          <div className="adm-cell">
                            <strong>{e.name || 'Unknown'}</strong>
                            <small>{e.email}</small>
                            <small dir="ltr">{e.phone}</small>
                          </div>
                        </td>
                        <td data-label="Program">
                          <div className="adm-cell">
                            <span>{e.programName || '—'}</span>
                            <small className={`adm-type adm-type--${e.programType}`}>{e.programType}</small>
                          </div>
                        </td>
                        <td data-label="Date">{formatDate(e.timestamp)}</td>
                        <td data-label="Status">
                          <select className={`adm-status-select adm-status--${status}`} value={status} onChange={(ev) => setStatus(e.id, ev.target.value)} aria-label={`Status for ${e.name}`}>
                            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        </td>
                        <td data-label="Actions" className="adm-actions-cell">
                          {e.email && <a className="adm-icon-btn" href={`mailto:${e.email}`} title="Email applicant"><i className="far fa-envelope"></i></a>}
                          {e.phone && <a className="adm-icon-btn" href={`tel:${e.phone}`} title="Call applicant"><i className="fas fa-phone"></i></a>}
                          <button className="adm-icon-btn adm-icon-btn--danger" onClick={() => setToDelete(e)} title="Delete" aria-label={`Delete ${e.name}`}><i className="far fa-trash-can"></i></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="adm-empty adm-empty--box">{counts.all ? 'No applicants match these filters.' : 'No enrollments yet.'}</p>
          )}
        </>
      ) : programsLoading ? (
        <p className="adm-empty">Loading programs…</p>
      ) : (
        <div className="adm-two">
          {renderPrograms('Courses', courses, 'course', (c) => pickName(c.title))}
          {renderPrograms('Diplomas', diplomas, 'diploma', (d) => pickName(d.name))}
        </div>
      )}

      <ConfirmDialog show={!!toDelete} title="Delete enrollment" busy={busy} onConfirm={confirmDelete} onCancel={() => setToDelete(null)}>
        Delete the enrollment from <strong>{toDelete?.name}</strong> ({toDelete?.email})? This cannot be undone.
      </ConfirmDialog>
    </div>
  );
};

export default EnrollManagement;
